"use client";

import { Check, X } from "lucide-react";
import Link from "next/link";

import { getComparisonContent } from "@/components/compare/copy";
import { AuthLink } from "@/components/shared/auth-link";
import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";

import { useI18n } from "@llmgateway/i18n/react";

import type { CompareValue, Competitor } from "@/components/compare/copy";

interface ComparisonTableProps {
	competitor: Competitor;
}

function renderFeatureValue(value: CompareValue) {
	if (typeof value === "boolean") {
		return value ? (
			<Check className="h-5 w-5 text-green-600 dark:text-green-400" />
		) : (
			<X className="h-5 w-5 text-red-600 dark:text-red-400" />
		);
	}
	return <span className="text-sm font-medium text-foreground">{value}</span>;
}

export function ComparisonTable({ competitor }: ComparisonTableProps) {
	const { locale } = useI18n();
	const content = getComparisonContent(competitor, locale);

	return (
		<section className="w-full py-12 md:py-24 lg:py-32 bg-background">
			<div className="container px-4 md:px-6 max-w-5xl mx-auto">
				<div className="text-center mb-12">
					<Badge variant="outline" className="mb-4">
						{content.eyebrow}
					</Badge>
					<h2 className="text-3xl font-bold tracking-tight mb-2 text-foreground">
						{content.title}
					</h2>
					<p className="text-muted-foreground">{content.description}</p>
				</div>

				<div className="mb-8 bg-primary/5 dark:bg-primary/10 rounded-lg p-6 border border-primary/20">
					<h3 className="font-bold text-lg mb-3 text-primary">
						{content.whyTitle}
					</h3>
					<div className="grid md:grid-cols-2 gap-4 text-sm">
						{content.whyBullets.map((bullet) => (
							<div key={bullet} className="flex items-start gap-2">
								<Check className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
								<span className="text-foreground">{bullet}</span>
							</div>
						))}
					</div>
				</div>

				<div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 sm:p-6 bg-muted/50 border-b border-border">
						<div className="hidden md:block" />
						<div className="text-center">
							<div className="border-2 border-primary rounded-lg p-4 bg-background shadow-sm h-full">
								<h3 className="font-bold text-lg mb-1 text-foreground">
									doteb
								</h3>
								<p className="text-sm text-muted-foreground mb-2">
									{content.llmgatewayCard.subtitle}
								</p>
								<p className="text-2xl font-bold text-primary">
									{content.llmgatewayCard.price}
								</p>
								<p className="text-xs text-muted-foreground mt-1">
									{content.llmgatewayCard.note}
								</p>
							</div>
						</div>
						<div className="text-center">
							<div className="border border-border rounded-lg p-4 bg-background h-full">
								<h3 className="font-bold text-lg mb-1 text-foreground">
									{content.competitorCard.name}
								</h3>
								<p className="text-sm text-muted-foreground mb-2">
									{content.competitorCard.subtitle}
								</p>
								<p className="text-2xl font-bold text-foreground">
									{content.competitorCard.price}
								</p>
								<p className="text-xs text-muted-foreground mt-1">
									{content.competitorCard.note}
								</p>
							</div>
						</div>
					</div>

					{content.categories.map((category, categoryIndex) => (
						<div key={category.category}>
							{categoryIndex > 0 && (
								<div className="border-t-2 border-border/50" />
							)}

							{category.features.map((feature) => (
								<div
									key={feature.title}
									className="grid grid-cols-3 gap-4 p-6 border-b border-border/50 hover:bg-muted/30 transition-colors"
								>
									<div>
										<h4 className="font-semibold text-foreground mb-1">
											{feature.title}
										</h4>
										<p className="text-sm text-muted-foreground">
											{feature.description}
										</p>
									</div>
									<div className="flex justify-center items-center">
										{renderFeatureValue(feature.llmgateway)}
									</div>
									<div className="flex justify-center items-center">
										{renderFeatureValue(feature.competitor)}
									</div>
								</div>
							))}
						</div>
					))}
				</div>

				<div className="text-center mt-8">
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<Button size="lg" className="bg-primary hover:bg-primary/90">
							<AuthLink href="/signup">{content.primaryCta}</AuthLink>
						</Button>
						<Button size="lg" variant="outline">
							<Link href="/pricing">{content.secondaryCta}</Link>
						</Button>
					</div>
					<p className="text-sm text-muted-foreground mt-3">
						{content.footnote}
					</p>
				</div>
			</div>
		</section>
	);
}
